class HHVariantPicker extends HTMLElement {
  constructor() {
    super()
    this.addEventListener('change', this.onVariantChange)
  }

  connectedCallback() {
    this._abortController = new AbortController();
    this.setupCustomDropdowns();

    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');

    if (variantId) {
      const variantData = this.getVariantData();
      const match = variantData.find(v => v.id == variantId);

      if (match) {
        this.currentVariant = match;
        this.updateVariantAvailabilityAttribute();
        this.updateOptions();
        this.updateOptionAvailability();

        // Wait for media slider + images to load
        this.waitForMediaReady().then(() => {
          this.updateMedia();
        });
      }
    }

    /* Set initial availability when no variant in URL (picker uses default selection) */
    if (!variantId) {
      this.updateOptions();
      this.updateMasterId();
      this.updateVariantAvailabilityAttribute();
      this.updateOptionAvailability();
      if (this.dataset.context === 'card') {
        this.syncCardVariantAvailabilityUi();
      }
    }
  }

  setupCustomDropdowns() {
    const containers = this.querySelectorAll('[data-custom-dropdown]');

    containers.forEach((container) => {
      const select = container.querySelector('select');
      const trigger = container.querySelector('.vp__input__select__trigger');
      const triggerLabel = container.querySelector('.vp__input__select__trigger-label');
      const list = container.querySelector('.vp__input__select__list');
      const options = container.querySelectorAll('.vp__input__select__option');

      if (!select || !trigger || !list) return;

      function syncTriggerLabel() {
        if (triggerLabel) {
          const selected = select.options[select.selectedIndex]
          const text = selected?.textContent?.trim() ?? ''
          triggerLabel.textContent = text || select.value
        }
        options.forEach((opt) => {
          opt.setAttribute('aria-selected', opt.dataset.value === select.value ? 'true' : 'false');
        });
      }

      function close() {
        container.classList.remove('vp__input__select--open');
        trigger.setAttribute('aria-expanded', 'false');
        list.setAttribute('aria-hidden', 'true');
      }

      function open() {
        container.classList.add('vp__input__select--open');
        trigger.setAttribute('aria-expanded', 'true');
        list.setAttribute('aria-hidden', 'false');
      }

      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const isOpen = container.classList.contains('vp__input__select--open');
        if (isOpen) close();
        else open();
      });

      options.forEach((opt) => {
        opt.addEventListener('click', (e) => {
          e.preventDefault();
          const value = opt.dataset.value;
          if (value != null) {
            select.value = value;
            syncTriggerLabel();
            close();
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });

      document.addEventListener('click', (e) => {
        if (container.classList.contains('vp__input__select--open') && !container.contains(e.target)) {
          close();
        }
      }, { signal: this._abortController.signal });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && container.classList.contains('vp__input__select--open')) {
          close();
          trigger.focus();
        }
      }, { signal: this._abortController.signal });

      syncTriggerLabel();
    });
  }

  getProductSectionRoot() {
    const id = this.dataset.section
    if (!id) return null
    return document.getElementById(`shopify-section-${id}`)
  }

  getProductMediaEl() {
    const root = this.getProductSectionRoot()
    return (
      root?.querySelector('product-media') ||
      document.querySelector('product-media')
    )
  }

  waitForMediaReady() {
    return new Promise((resolve) => {
      const checkReady = () => {
        const productMedia = this.getProductMediaEl()
        if (!productMedia) {
          requestAnimationFrame(checkReady)
          return
        }

        const slides = productMedia.querySelectorAll('.blaze-slide')
        const slidesReady =
          productMedia.querySelectorAll('.blaze-slide img').length > 0
        const singleSlide = slides.length <= 1
        const blazeReady = singleSlide || productMedia.blazeSlider

        if (slidesReady && blazeReady) {
          resolve()
        } else {
          requestAnimationFrame(checkReady)
        }
      }

      checkReady()
    })
  }

  onVariantChange() {
    this.updateOptions()
    this.updateMasterId()
    this.updateVariantAvailabilityAttribute()
    this.updateOptionAvailability()
    this.toggleAddButton(true, '', false)
    this.updatePickupAvailability()

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true)
      this.setUnavailable()
      if (this.dataset.context === 'card') {
        this.updateCardAtcOosClass(true)
      }
    } else {
      if (
        this.dataset.combinedListing === 'true' &&
        this.dataset.context !== 'card'
      ) {
        const urlVariant = new URLSearchParams(window.location.search).get('variant')
        if (String(this.currentVariant.id) !== urlVariant) {
          window.location.assign(`${this.dataset.url}?variant=${this.currentVariant.id}`)
          return
        }
      }

      this.updateVariantInput()
      const isCardContext = this.dataset.context === 'card'
      if (isCardContext) {
        this.syncCardVariantAvailabilityUi()
      } else {
        this.updateMedia()
        this.updateURL()
        this.updateReviewWidget()
        this.renderProductInfo()
      }
    }
  }

  updateVariantAvailabilityAttribute() {
    const available = this.currentVariant?.available ?? false
    this.setAttribute('data-variant-available', available ? 'true' : 'false')
  }

  getVariantOptionsArray(variant) {
    if (Array.isArray(variant.options) && variant.options.length > 0) {
      return variant.options
    }
    const opts = [variant.option1, variant.option2, variant.option3].filter(
      (v) => v != null && v !== ''
    )
    return opts
  }

  updateOptionAvailability() {
    const variantData = this.getVariantData()
    const containers = this.querySelectorAll('[data-custom-dropdown]')
    if (!variantData.length || !containers.length) return

    const currentOptions = Array.from(containers, (container) => {
      const sel = container.querySelector('select')
      return sel ? (sel.value || '').trim() : ''
    })

    containers.forEach((container, optionIndex) => {
      const optionButtons = container.querySelectorAll('.vp__input__select__option')
      optionButtons.forEach((opt) => {
        const value = opt.dataset.value != null ? String(opt.dataset.value).trim() : ''
        if (value === '') return

        const candidate = currentOptions.slice()
        candidate[optionIndex] = value
        const variant = variantData.find((v) => {
          const vOpts = this.getVariantOptionsArray(v)
          if (vOpts.length < candidate.length) return false
          return candidate.every((c, i) => String(vOpts[i]).trim() === c)
        })

        const available = variant == null ? true : variant.available
        opt.setAttribute('data-variant-available', available ? 'true' : 'false')
      })
    })
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'))
    if (fieldsets && fieldsets.length) {
      this.options = fieldsets.map((fieldset) => {
        return Array.from(fieldset.querySelectorAll('input')).find(
          (radio) => radio.checked
        ).value
      })
    } else {
      this.options = Array.from(
        this.querySelectorAll('select'),
        (select) => select.value
      )
    }
  }

  updateMasterId() {
    const variantData = this.getVariantData()

    this.currentVariant = variantData.find((variant) => {
      const vOpts = this.getVariantOptionsArray(variant)
      return this.options.every((selectedOption, index) => {
        const v = vOpts[index]
        if (v == null && (selectedOption === '' || selectedOption == null)) return true
        return String(v).trim() === String(selectedOption).trim()
      })
    })
  }

  updateReviewWidget() {
    if (!this.currentVariant) return;

    const productId = this.currentVariant.product_id;

    if (!productId) {
      console.warn('No product ID found for variant:', this.currentVariant.id);
      return;
    }

    // --- Update Reviews Section (Replace the Element)
    const oldReviewDiv = document.querySelector('[data-bv-show="reviews"]');
    if (oldReviewDiv) {
      const newReviewDiv = document.createElement('div');
      newReviewDiv.className = 'bv-reviews';
      newReviewDiv.setAttribute('data-bv-show', 'reviews');
      newReviewDiv.setAttribute('data-bv-product-id', productId);

      oldReviewDiv.replaceWith(newReviewDiv);

      this.updateBVWidget('[data-bv-show="reviews"]', productId, 'show_reviews');
    }

    // --- Update Rating Summary (Just Update Data Attribute)
    this.updateBVWidget('[data-bv-show="rating_summary"]', productId, 'show_rating_summary');
  }

  updateBVWidget(selector, productId, showType) {
    const element = document.querySelector(selector);
    if (!element) return;

    element.setAttribute('data-bv-productid', productId);

    if (window.BV && typeof BV.ui === 'function') {
      const options = {
        productId: productId,
        containerPrefix: ''
      };

      if (showType === 'show_reviews') {
        options.contentContainerDiv = element;
      }

      BV.ui('rr', showType, options);
    } else {
      console.warn(`BV UI not available for ${showType}`);
    }
  }


  updateMedia() {
    if (!this.currentVariant) return

    const runSlideTo = () => {
      const productMedia = this.getProductMediaEl()
      if (!productMedia?.goToSlide) return false

      const slides = productMedia.querySelectorAll('.blaze-slide')
      if (!slides.length) return false

      const featured =
        this.currentVariant.featured_media || this.currentVariant.featured_image
      let mediaId = featured?.id

      let targetIndex = -1

      const slideIndexFromEl = (slide) => {
        const raw = slide.getAttribute('data-slide-index')
        if (raw == null || raw === '') return -1
        const n = parseInt(raw, 10)
        return Number.isNaN(n) ? -1 : n
      }

      if (mediaId != null) {
        slides.forEach((slide) => {
          const sid = slide.getAttribute('data-media-id')
          if (sid != null && String(sid) === String(mediaId)) {
            const idx = slideIndexFromEl(slide)
            if (idx >= 0) targetIndex = idx
          }
        })
      }

      const skuToFind = this.currentVariant.sku
      if (targetIndex === -1 && skuToFind) {
        slides.forEach((slide) => {
          const img = slide.querySelector('img')
          if (img?.getAttribute('alt')?.includes(skuToFind)) {
            const idx = slideIndexFromEl(slide)
            if (idx >= 0) targetIndex = idx
          }
        })
      }

      if (targetIndex === -1) return false

      productMedia.goToSlide(targetIndex)

      return true
    }

    if (runSlideTo()) return

    this.waitForMediaReady().then(() => {
      runSlideTo()
    })
  }


  updateURL() {
    if (!this.currentVariant) return
    window.history.replaceState(
      {},
      '',
      `${this.dataset.url}?variant=${this.currentVariant.id}`
    )

    const locationChangeEvent = document.createEvent('Event');
    locationChangeEvent.initEvent('locationchange', true, true);
    window.dispatchEvent(locationChangeEvent);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment`
    );

    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      if (input) {
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    if (this.dataset.context !== 'card') {
      const skuElement = document.querySelector('.product-sku p.utility');
      if (skuElement) {
        skuElement.innerHTML = `<strong>SKU: </strong>${this.currentVariant.sku || 'N/A'}`;
      }
    }
  }

  formatMoney(cents, moneyFormat) {
    if (cents == null) return ''
    const amount = (cents / 100).toFixed(2)
    if (moneyFormat && typeof moneyFormat === 'string') {
      return moneyFormat
        .replace(/\{\{\s*amount\s*\}\}/, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, (cents / 100).toFixed(0))
    }
    return amount
  }

  updateCardPrice() {
    const form = document.getElementById(`product-form-${this.dataset.section}`)
    if (!form || !this.currentVariant) return
    const priceEl = form.querySelector('[data-card-price]')
    if (!priceEl) return
    const moneyFormat = this.dataset.moneyFormat || form.dataset.moneyFormat
    priceEl.textContent = this.formatMoney(this.currentVariant.price, moneyFormat)
  }

  updateCardBISVariant() {
    const form = document.getElementById(`product-form-${this.dataset.section}`)
    if (!form || !this.currentVariant) return
    const skuInput = form.querySelector('.js-bis-variant-fields input[name="Sku"]')
    if (skuInput) skuInput.value = this.currentVariant.sku || ''
  }

  /**
   * Marks all product-card add-to-cart shells as OOS when the card variant is unavailable.
   * Combined listings use the parent variant JSON from variant-picker.liquid; updates run on change.
   */
  syncCardVariantAvailabilityUi() {
    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true)
      this.updateCardAtcOosClass(true)
      return
    }
    this.updateCardPrice()
    this.toggleAddButton(
      !this.currentVariant.available,
      window.variantStrings?.soldOut ?? 'Sold out'
    )
    this.updateCardBISVariant()
    this.updateCardAtcOosClass(!this.currentVariant.available)
  }

  updateCardAtcOosClass(isOos) {
    if (this.dataset.context !== 'card') return
    const form = document.getElementById(`product-form-${this.dataset.section}`)
    if (!form) return
    form.querySelectorAll('.product-card__atc').forEach((el) => {
      el.classList.toggle('oos', Boolean(isOos))
    })
  }


  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector('pickup-availability')
    if (!pickUpAvailability) return

    if (this.currentVariant?.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id)
    } else {
      pickUpAvailability.removeAttribute('available')
      pickUpAvailability.innerHTML = ''
    }
  }

  renderProductInfo() {
    fetch(
      `${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`
    )
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html')

        this.replaceInnerElement(html, `#price-${this.dataset.section}`)
        this.replaceInnerElement(html, `.js-bis-variant-fields`)

        document
          .getElementById(`price-${this.dataset.section}`)
          ?.classList.remove('visibility-hidden')
        this.toggleAddButton(
          !this.currentVariant.available,
          window.variantStrings.soldOut
        )
      })
  }

  replaceInnerElement(html, selector) {
    const destination = document.querySelector(selector)
    const source = html.querySelector(selector)

    if (source && destination) destination.innerHTML = source.innerHTML
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const form = document.getElementById(`product-form-${this.dataset.section}`)
    const addButtons = form?.querySelectorAll('[name="add"]') || []
    const wrappersWithBIS = form?.querySelectorAll('.js-buttons-wrapper.has-bis-form') || []
    const hasBIS = wrappersWithBIS.length > 0

    if (!addButtons.length) return

    if (disable) {
      if (hasBIS && modifyClass) wrappersWithBIS.forEach((w) => w.classList.add('show-bis-form'))

      addButtons.forEach((addButton) => {
        const inMobileBISWrapper = addButton.closest('.product-card__atc-wrapper--mobile') != null
        if (inMobileBISWrapper) return
        addButton.setAttribute('disabled', true)
        const isIconOnlyButton = addButton.closest('.product-card__atc--round') != null
        if (text && !isIconOnlyButton) addButton.textContent = text
      })
    } else {
      if (hasBIS && modifyClass) wrappersWithBIS.forEach((w) => w.classList.remove('show-bis-form'))

      addButtons.forEach((addButton) => {
        const inMobileBISWrapper = addButton.closest('.product-card__atc-wrapper--mobile') != null
        if (inMobileBISWrapper) return
        addButton.removeAttribute('disabled')
        const isIconOnlyButton = addButton.closest('.product-card__atc--round') != null
        if (!isIconOnlyButton) addButton.textContent = window.variantStrings.addToCart
      })
    }
  }

  setUnavailable() {
    const form = document.getElementById(`product-form-${this.dataset.section}`)
    const addButtons = form?.querySelectorAll('[name="add"]') || []
    addButtons.forEach((addButton) => {
      const isIconOnlyButton = addButton.closest('.product-card__atc--round') != null
      if (!isIconOnlyButton) addButton.textContent = window.variantStrings.unavailable
    })
    document
      .getElementById(`price-${this.dataset.section}`)
      ?.classList.add('visibility-hidden')
  }

  getVariantData() {
    this.variantData =
      this.variantData ||
      JSON.parse(this.querySelector('[type="application/json"]').textContent)
    return this.variantData
  }

  disconnectedCallback() {
    this._abortController?.abort();
  }
}

customElements.define('hh-variant-picker', HHVariantPicker)
